import { useQuery, useQueryClient, useMutation } from 'react-query'
import { useAppStore } from '@/store/useAppStore'
import { getProfile, updateProfileName } from '../api/userApi'
import type { UserProfile } from '../types'

export const PROFILE_QUERY_KEY = ['user', 'profile'] as const

/**
 * React Query-запрос текущего профиля пользователя.
 * @param enabled - Включён ли запрос (обычно — после установленной авторизации)
 * @returns UseQueryResult<UserProfile>
 */
export const useProfileQuery = (enabled = true) => {
  return useQuery<UserProfile>(PROFILE_QUERY_KEY, getProfile, {
    enabled,
    staleTime: 60_000,
    retry: false,
    // Свежий ответ кладём в стор. Без этого профиль в сторе оставался таким,
    // каким его записали при входе, и жил дальше в localStorage: в Telegram
    // его никто не перечитывал. Отсюда и брались плашки про заканчивающуюся
    // подписку у людей, которые давно продлились — дата бралась из свежего
    // ответа `/subscription`, а грейс из снимка месячной давности.
    onSuccess: profile => useAppStore.getState().setProfile(profile)
  })
}

/**
 * Возвращает функцию, инвалидирующую кеш профиля.
 * Используется после мутаций, меняющих профиль (link-email, link-telegram, update-name).
 * @returns Функция invalidate
 */
export const useInvalidateProfile = () => {
  const qc = useQueryClient()
  return () => qc.invalidateQueries(PROFILE_QUERY_KEY)
}

/**
 * Мутация обновления имени пользователя. После успеха кеш профиля обновляется.
 * @returns UseMutationResult для `PATCH /user/profile { name }`
 */
export const useUpdateProfileName = () => {
  const qc = useQueryClient()
  return useMutation(updateProfileName, {
    onSuccess: (profile) => {
      qc.setQueryData(PROFILE_QUERY_KEY, profile)
    }
  })
}
